import { useEffect, useState, useCallback } from 'react'
import { api, getAdminToken } from './api'
import { LoginView } from './components/LoginView'
import { OverviewView } from './components/OverviewView'
import { PlatformAdminsView } from './components/PlatformAdminsView'
import { Sidebar } from './components/Sidebar'
import { TenantAdminsView } from './components/TenantAdminsView'
import { TenantsView } from './components/TenantsView'
import parelLogo from '../assets/parelteknoloji-mavia.svg'
import { AdminTab, PlatformAdmin, Tenant } from './types'

export function App() {
  const [activeTab, setActiveTab] = useState<AdminTab>('overview')
  const [authChecking, setAuthChecking] = useState<boolean>(true)

  const [currentAdmin, setCurrentAdmin] = useState<PlatformAdmin | null>(null)
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [platformAdmins, setPlatformAdmins] = useState<PlatformAdmin[]>([])
  const [tenantAdminsCount, setTenantAdminsCount] = useState<number>(0)

  // Selected tenant for TenantAdmins tab filtering
  const [selectedTenantForAdmins, setSelectedTenantForAdmins] = useState<string | null>(null)

  // Quick modals from overview
  const [createTenantModalOpen, setCreateTenantModalOpen] = useState(false)
  const [createPlatformAdminModalOpen, setCreatePlatformAdminModalOpen] = useState(false)

  const loadAllData = useCallback(async () => {
    try {
      const [tenantsList, adminsList] = await Promise.all([
        api.listTenants(),
        api.listPlatformAdmins(),
      ])
      setTenants(tenantsList)
      setPlatformAdmins(adminsList)

      // Count total tenant admins across all tenants
      let count = 0
      for (const t of tenantsList) {
        try {
          const tAdmins = await api.listTenantAdmins(t.id)
          count += tAdmins.length
        } catch {
          // ignore
        }
      }
      setTenantAdminsCount(count)
    } catch {
      // ignore
    }
  }, [])

  // Check auth on mount
  useEffect(() => {
    const token = getAdminToken()
    if (!token) {
      setAuthChecking(false)
      return
    }

    api
      .getMe()
      .then((admin) => {
        setCurrentAdmin(admin)
        loadAllData()
      })
      .catch(() => {
        api.logout()
        setCurrentAdmin(null)
      })
      .finally(() => {
        setAuthChecking(false)
      })
  }, [loadAllData])

  const handleLoginSuccess = (admin: PlatformAdmin) => {
    setCurrentAdmin(admin)
    loadAllData()
  }

  const handleLogout = () => {
    api.logout()
    setCurrentAdmin(null)
    setTenants([])
    setPlatformAdmins([])
    setTenantAdminsCount(0)
  }

  const handleManageTenantAdmins = (tenantId: string) => {
    setSelectedTenantForAdmins(tenantId)
    setActiveTab('tenant-admins')
  }

  if (authChecking) {
    return (
      <div className="loading-splash">
        <div className="loading-spinner" aria-hidden="true" />
        <span>Platform yöneticisi oturumu kontrol ediliyor…</span>
      </div>
    )
  }

  if (!currentAdmin) {
    return <LoginView onLoginSuccess={handleLoginSuccess} />
  }

  const tabTitles: Record<AdminTab, { title: string; category: string }> = {
    overview: { title: 'Platform Genel Bakış', category: 'Sistem & İzleme' },
    tenants: { title: 'Tenant & Firma Yönetimi', category: 'Organizasyon' },
    'tenant-admins': { title: 'Tenant Yöneticileri', category: 'Organizasyon' },
    'platform-admins': { title: 'Platform Yöneticileri', category: 'Güvenlik & Yetkilendirme' },
  }

  const currentTabInfo = tabTitles[activeTab] || { title: '', category: '' }

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <Sidebar
        activeTab={activeTab}
        onSelectTab={setActiveTab}
        admin={currentAdmin}
        onLogout={handleLogout}
        counts={{
          tenants: tenants.length,
          platformAdmins: platformAdmins.length,
          tenantAdmins: tenantAdminsCount,
        }}
      />

      {/* Main Container */}
      <div className="app-container">
        <header className="app-topbar">
          <div className="topbar-breadcrumbs">
            <span className="breadcrumb-category">{currentTabInfo.category}</span>
            <span className="breadcrumb-separator" aria-hidden="true">/</span>
            <h1 className="breadcrumb-title">{currentTabInfo.title}</h1>
          </div>

          <div className="topbar-actions">
            <span className="platform-badge">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span>Platform Superadmin</span>
            </span>

            <img
              src={parelLogo}
              alt="Parel Teknoloji"
              className="topbar-brand-icon"
            />
          </div>
        </header>

        <main className="app-main-content">
          {activeTab === 'overview' && (
            <OverviewView
              tenants={tenants}
              platformAdmins={platformAdmins}
              tenantAdminsCount={tenantAdminsCount}
              onNavigate={setActiveTab}
              onOpenCreateTenant={() => {
                setActiveTab('tenants')
                setCreateTenantModalOpen(true)
              }}
              onOpenCreatePlatformAdmin={() => {
                setActiveTab('platform-admins')
                setCreatePlatformAdminModalOpen(true)
              }}
            />
          )}

          {activeTab === 'tenants' && (
            <TenantsView
              tenants={tenants}
              onRefresh={loadAllData}
              onManageTenantAdmins={handleManageTenantAdmins}
              createModalOpen={createTenantModalOpen}
              onCloseCreateModal={() => setCreateTenantModalOpen(false)}
            />
          )}

          {activeTab === 'tenant-admins' && (
            <TenantAdminsView
              tenants={tenants}
              selectedTenantId={selectedTenantForAdmins}
              onSelectTenantId={setSelectedTenantForAdmins}
              onRefreshData={loadAllData}
            />
          )}

          {activeTab === 'platform-admins' && (
            <PlatformAdminsView
              platformAdmins={platformAdmins}
              currentAdmin={currentAdmin}
              onRefresh={loadAllData}
              createModalOpen={createPlatformAdminModalOpen}
              onCloseCreateModal={() => setCreatePlatformAdminModalOpen(false)}
            />
          )}
        </main>
      </div>
    </div>
  )
}

export default App
